<script lang="ts">
	import { clone } from "@bufbuild/protobuf";
	import { getContext, onMount } from "svelte";
	import { twMerge } from "tailwind-merge";
	import { PageSchema } from "#gen/pages/v2/page_pb.js";
	import Label from "#lib/components/Label.svelte";
	import P from "#lib/components/P.svelte";
	import PlaintextEditor from "#lib/components/content-editors/PlaintextEditor.svelte";
	import ButtonAppearanceEditor from "#lib/components/flow/editor/appearance/ButtonAppearanceEditor.svelte";
	import StateEditor from "#lib/components/flow/editor/appearance/StateEditor.svelte";
	import type { ComponentEditingProps } from "#lib/components/page/ComponentProps.js";
	import EditorInset from "#lib/components/page/editing/EditorInset.svelte";
	import FeatureFlagGate from "#lib/permissions/FeatureFlagGate.svelte";
	import { setStringValue, stringValue, structValue } from "#lib/struct.js";
	import type { TokenProvider } from "#lib/tokenProvider.js";
	import type { Writable } from "svelte/store";

	const provider: Writable<TokenProvider> = getContext("tokenProvider");

	let { id, page, onChange }: ComponentEditingProps = $props();

	let initiallyUnset = $state(false);

	const config = $derived(structValue($page.config, id) ?? {});
	const text = $derived(stringValue(config, "text"));
	const className = $derived(stringValue(config, "class"));

	onMount(() => {
		initiallyUnset = stringValue(structValue($page.config, id), "text") === undefined;
	});

	export function validate(): boolean {
		return true;
	}

	function update(e: CustomEvent<string | undefined>) {
		if (!e.detail && initiallyUnset) {
			delete config.text;
		} else {
			setStringValue(config, "text", e.detail ?? "");
		}
		$page = clone(PageSchema, $page);
	}

	function handleChange(e: CustomEvent<string | undefined>) {
		onChange?.(e.detail);
	}
</script>

<EditorInset>
	<div class={twMerge("flex flex-col gap-6 px-6", className)}>
		<FeatureFlagGate flag="build_your_own_page">
			<div class="flex w-full flex-col gap-5 rounded-lg border p-3">
				<P class="text-sm font-bold text-black"><FeatureFlagGate flag="editor_toolbar_revamp_q4_2026">Content{#snippet fallback()}Attributes{/snippet}</FeatureFlagGate></P>
				<div class="flex flex-col gap-2">
					<P class="text-xs font-semibold tracking-wide text-gray-500 uppercase">Text</P>
					<div class="flex flex-col gap-2">
						<div class="flex flex-col border-l-[5px] border-l-gray-300 pl-6">
							<PlaintextEditor provider={$provider} value={text} on:input={update} on:change={handleChange} aria-labelledby="{id}-label" />
						</div>
					</div>
				</div>
			</div>
			<div class="flex w-full flex-col gap-2 rounded-lg border p-3">
				<ButtonAppearanceEditor {id} {page} {onChange} />
			</div>
			<StateEditor {id} {page} {onChange} />
			{#snippet fallback()}
				<Label id="{id}-label" class="text-gray-900">Text</Label>
				<PlaintextEditor provider={$provider} value={text} on:input={update} on:change={handleChange} aria-labelledby="{id}-label" />
			{/snippet}
		</FeatureFlagGate>
	</div>
</EditorInset>
